import React from 'react';
import { ChevronRight, LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ActionRowProps {
  icon?: LucideIcon;
  iconColor?: string;
  label: string;
  value?: React.ReactNode;
  to?: string;
  onClick?: () => void;
  chevron?: boolean;
  badge?: React.ReactNode;
  destructive?: boolean;
  className?: string;
}

export function ActionRow({
  icon: Icon,
  iconColor = 'text-[var(--text-tertiary)]',
  label,
  value,
  to,
  onClick,
  chevron = true,
  badge,
  destructive = false,
  className = '',
}: ActionRowProps) {
  const content = (
    <>
      {Icon && (
        <div className={`flex-shrink-0 ${destructive ? 'text-red-500' : iconColor}`}>
          <Icon size={20} />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <span className={`text-[15px] ${destructive ? 'text-red-600 dark:text-red-400 font-medium' : 'text-[var(--text-primary)]'}`}>
          {label}
        </span>
        {value && (
          <span className="block text-sm text-[var(--text-secondary)] truncate mt-0.5">
            {value}
          </span>
        )}
      </div>
      {badge && <div className="flex-shrink-0 ml-2">{badge}</div>}
      {chevron && (to || onClick) && (
        <ChevronRight size={18} className="text-[var(--text-tertiary)] flex-shrink-0 ml-1" />
      )}
    </>
  );

  const dividerClass = Icon ? 'inset-divider' : 'inset-divider inset-divider-flush';
  const baseClass = `flex items-center gap-3 px-4 py-3.5 min-h-[48px] ${dividerClass} ${
    (to || onClick) ? 'press-row cursor-pointer' : ''
  } ${className}`;

  if (to) {
    return <Link to={to} className={baseClass}>{content}</Link>;
  }

  if (onClick) {
    return <button onClick={onClick} className={`${baseClass} w-full text-left`}>{content}</button>;
  }

  return <div className={baseClass}>{content}</div>;
}
